/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { CSSProperties, FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";
import { ReactSelectSearchProps } from "../../types/components/reactSelectSearchProps";
import { REACT_SELECT_COLORS, SELECT_DARK_STYLES } from "../../constant";
import {
  FormatCustomAELabel,
  filterCustomAEOption,
} from "../../components/Displays/FormatCustomAELabel";
import { ReactSelectStates } from "../../types/components/select";
import { BigText } from "../Displays/BigText";

const ReactSelectSearch: FunctionComponent<ReactSelectSearchProps> = (
  props: ReactSelectSearchProps
) => {
  const { t } = useTranslation();
  const {
    defaultValue,
    options,
    handleSelectChange,
    handleFocus,
    handleBlur,
    name = "",
    value,
    placeholder,
    isMulti,
    isSearchable,
    isDisabled,
    isClearable = true,
    style,
    key,
    filterOption,
    className = "",
    placeholderReadonly,
    isDisabledDropdownIndicator,
    menuPortalTarget = document.body,
    multiValueLabelStyle,
    openMenuOnClick = true,
    isDark = false,
    isFormatCustomAE = false,
    placeholderOpacity = 0.5,
    customFormatLabel,
    classNamePrefix = "react-select-search",
    menuPlacement = "auto",
    ...rest
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let components: any = {
    IndicatorSeparator: () => null,
  };
  if (isDisabledDropdownIndicator) {
    components = {
      ...components,
      DropdownIndicator: () => null,
    };
  }

  const buildSelectStyles = useMemo(() => {
    let styles = {
      menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 9999 }),
      container: (base: CSSProperties) => ({ ...base, minWidth: 100 }),
      valueContainer: (base: CSSProperties, state: ReactSelectStates) => ({
        ...base,
        ...(state.hasValue && { paddingLeft: "0.8rem" }), // 0.8 * 16 or 14px.
        ...(state.hasValue && state.isMulti && { maxHeight: "4.5em" }),
        ...(state.hasValue && state.isMulti && { overflowY: "auto" }),
        ...(state.isDisabled && { opacity: 1 }),
        ...style,
        pointerEvents: "all",
      }),
      multiValueLabel: (provided: CSSProperties) => ({
        ...provided,
        ...multiValueLabelStyle,
        backgroundColor: REACT_SELECT_COLORS.MULTI_VALUE_BG,
      }),
      placeholder: (base: CSSProperties) => ({
        ...base,
        opacity: placeholderOpacity,
      }),
      control: (base: CSSProperties, state: ReactSelectStates) => {
        return {
          ...base,
          backgroundColor: "transparent",
          borderColor: state.isFocused
            ? REACT_SELECT_COLORS.FOCUS_BORDER
            : base.borderColor,
          boxShadow: state.isFocused
            ? `0 0 0 1px ${REACT_SELECT_COLORS.FOCUS_BORDER}`
            : "none",
          "&:hover": {
            borderColor: state.isFocused
              ? REACT_SELECT_COLORS.FOCUS_BORDER
              : base.borderColor,
          },
        };
      },
      menu: (base: CSSProperties, state: ReactSelectStates) => ({
        ...base,
        backgroundColor: state?.isFocused
          ? REACT_SELECT_COLORS.DARK_MENU_BG
          : REACT_SELECT_COLORS.LIGHT_MENU_BG,
        marginTop: 5,
      }),
      option: (base: CSSProperties, state: ReactSelectStates) => ({
        ...base,
        backgroundColor: state.isSelected
          ? REACT_SELECT_COLORS.LIGHT_OPTION_SELECTED_BG
          : state?.isFocused
          ? REACT_SELECT_COLORS.LIGHT_OPTION_FOCUSED_BG
          : REACT_SELECT_COLORS.LIGHT_OPTION_BG,
        color: state.isSelected
          ? REACT_SELECT_COLORS.WHITE
          : REACT_SELECT_COLORS.LIGHT_TEXT,
        "&:active": {
          backgroundColor: state.isSelected
            ? REACT_SELECT_COLORS.LIGHT_OPTION_SELECTED_BG
            : REACT_SELECT_COLORS.LIGHT_OPTION_FOCUSED_BG,
        },
      }),
      input: (base: CSSProperties) => ({
        ...base,
      }),
    };

    if (isDark) {
      // build style with dark mode.
      // @ts-ignore
      styles = {
        ...styles,
        ...SELECT_DARK_STYLES,
      };
    }

    return styles;
  }, [isDark, multiValueLabelStyle, style, placeholderOpacity]);

  return (
    <>
      <ReactSelect
        key={key}
        isMulti={isMulti}
        classNamePrefix={classNamePrefix}
        className={`${className} ${isClearable ? "" : "hide-clear"}`}
        defaultValue={defaultValue}
        options={options}
        formatOptionLabel={
          isFormatCustomAE ? FormatCustomAELabel : customFormatLabel
        }
        onChange={(newValue, action) => {
          if (isDisabled) return;
          handleSelectChange(newValue, action);
        }}
        placeholder={
          <BigText
            className={`p-l-xxs w-100 `}
            text={
              isDisabled
                ? placeholderReadonly
                : placeholder || t("react_select_message.search")
            }
          />
        }
        name={name}
        onMenuOpen={() => handleFocus && handleFocus()}
        onMenuClose={() => handleBlur && handleBlur()}
        openMenuOnClick={openMenuOnClick}
        value={value}
        isDisabled={isDisabled}
        components={components}
        isClearable={isClearable}
        isSearchable={isSearchable}
        // @ts-ignore
        styles={buildSelectStyles}
        menuPosition="fixed"
        menuPortalTarget={menuPortalTarget}
        noOptionsMessage={() => t("react_select_message.no_options")}
        filterOption={
          filterOption === undefined && isFormatCustomAE
            ? filterCustomAEOption
            : filterOption
        }
        menuPlacement={menuPlacement}
        {...rest}
      />
    </>
  );
};

export default ReactSelectSearch;
